import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Download, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (iso) => {
    try {
        return new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });
    } catch {
        return iso;
    }
};

export const DownloadsAdmin = ({ headers, onUnauth }) => {
    const [data, setData] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/admin/downloads`, { headers })
            .then((r) => setData(r.data))
            .catch((err) => {
                if (err?.response?.status === 401) { toast.error("Session expired."); onUnauth(); }
                else toast.error("Couldn't load download activity.");
            });
    }, []);

    if (!data) return <p className="text-lilac-700 text-sm">Loading download activity…</p>;

    if (data.total === 0) {
        return (
            <div data-testid="downloads-empty" className="rounded-3xl bg-white border border-border p-12 text-center">
                <span className="mx-auto grid place-items-center w-12 h-12 rounded-full bg-lilac-100 text-lilac-900">
                    <Download className="w-5 h-5" />
                </span>
                <h3 className="mt-4 font-display text-2xl text-lilac-900">No downloads yet.</h3>
                <p className="mt-2 text-sm text-lilac-700">
                    Every time someone saves your cover art or headshot from the Press Kit, it will show up here.
                </p>
            </div>
        );
    }

    return (
        <div className="space-y-8" data-testid="downloads-activity">
            <div>
                <h3 className="font-display text-2xl text-lilac-900 mb-4">Totals by resource</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="rounded-3xl bg-lilac-900 text-white p-6" data-testid="downloads-total-card">
                        <div className="font-display text-4xl leading-none">{data.total}</div>
                        <div className="mt-2 text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">Total downloads</div>
                    </div>
                    {data.summary.map((s) => (
                        <div key={s.asset} className="rounded-3xl bg-white border border-border p-6" data-testid={`downloads-summary-${s.asset}`}>
                            <div className="font-display text-4xl gold-text leading-none">{s.count}</div>
                            <div className="mt-2 text-sm text-lilac-900 font-medium">{s.label || s.asset}</div>
                            <div className="mt-1 text-xs text-lilac-700 flex items-center gap-1.5">
                                <Clock className="w-3 h-3" /> Last: {fmt(s.last_at)}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div>
                <h3 className="font-display text-2xl text-lilac-900 mb-4">Recent activity</h3>
                <div className="rounded-3xl bg-white border border-border overflow-hidden">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="bg-lilac-50 text-left">
                                <th className="px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-lilac-700 font-bold">Resource</th>
                                <th className="px-6 py-3 text-[10px] uppercase tracking-[0.16em] text-lilac-700 font-bold">When</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.events.map((e) => (
                                <tr key={e.id} className="border-t border-lilac-100">
                                    <td className="px-6 py-3 text-lilac-900">{e.label || e.asset}</td>
                                    <td className="px-6 py-3 text-lilac-700">{fmt(e.created_at)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
